import dayjs from "dayjs";
import React, { useEffect } from "react";

import Button from "../../Button/Button";
import Divider from "../../Divider/Divider";
import { DatePickerType } from "../shared";

import DayView from "./DayView";
import MonthView from "./MonthView";
import YearView from "./YearView";

export enum DateSelectorView {
  /**
   * View the month (days are on screen)
   */
  day = "day",
  /**
   * View the year (months are on screen)
   */
  month = "month",
  /**
   * View the years (years are on screen)
   */
  year = "year",
}

export type DateSelectorProps = {
  view?: DateSelectorView | `${DateSelectorView}`;
  type: DatePickerType | `${DatePickerType}`;
  minDate?: Date;
  maxDate?: Date;
  defaultDate?: Date;
  onChange?: (value: Date | [Date, Date]) => void;
  onCancel?: () => void;
  /** If true, will ignore the users timezone and only use UTC. Only use if you need this specific behaviour */
  naive?: boolean;
} & (
    | {
      type: DatePickerType.single | `${DatePickerType.single}`,
      value: Date,
      onChange: (value: Date) => void,
      okText?: never;
      cancelText?: never;
    } | {
      type: DatePickerType.range | `${DatePickerType.range}`,
      value: [Date, Date],
      onChange: (value: [Date, Date]) => void,
      okText?: string;
      cancelText?: string;
    }
  );

/**
 * Select a date from a calendar view. This is NOT the input. See <DatePicker /> for that
 *
 * @description By default, this component internally utilises UTC time, but will display the time in the user's timezone.
 *   For example:
 *   - The user is in UTC+1
 *   - The user selects 2022-01-01T00:00:00.000
 *   - The value will be 2021-12-31T23:00:00.000Z (the same 'instant' in time)
 *   - The user will see 2022-01-01T00:00:00.000
 *   - Converted to the user's timezone, it would be 2022-01-01T00:00:00.000 (correct)
 *
 *   This is to ensure that the date is real, but the user sees the date in their timezone.
 *
 *   If "naive", only UTC is used. The user will see the date in UTC. For example:
 *    - The user is in UTC+1
 *    - The user selects 2022-01-01T00:00:00.000
 *    - The value will be 2022-01-01T00:00:00.000Z
 *    - The user will see 2022-01-01T00:00:00.000
 *    - Converted to the user's timezone, it would be 2022-01-01T01:00:00.000 (one hour later)
 *
 *   The time is "wrong", but visually matches.
 *
 *   Use the default to have "real" times, use naive to make date.getUTCHour() return what the user sees.
 *
 */
const DateSelector: React.FC<DateSelectorProps> = ({
  view,
  type,
  value,
  onChange,
  onCancel,
  minDate,
  maxDate,
  defaultDate = new Date(),
  naive,
  okText,
  cancelText,
}) => {
  // Value used in confirmation stage
  const [stagedValue, setStagedValue] = React.useState<Date | [Date, Date]>();
  // Value used to track temporary values when dealing with ranges. This is only ever partial data
  const [internalValue, setInternalValue] = React.useState<Date | undefined | [Date | undefined, Date | undefined]>();
  // The first date in the range. This is used to define what date is shown by default in the UI
  const [firstDate, setFirstDate] = React.useState<Date>(defaultDate);

  const handleSelectDate = (date: Date): void => {
    if (type === DatePickerType.single) {
      setInternalValue(undefined);
      handleChange(date);
    } else if (!internalValue) {
      setInternalValue([date, undefined]);
    } else if (!internalValue[0]) {
      setInternalValue([date, internalValue[1]]);
    } else {
      setInternalValue(undefined);
      handleChange([internalValue[0], date]);
    }
  };
  const handleHoverDate = (date: Date): void => {
    if (type === DatePickerType.single) return;
    if (!internalValue) return;
    if (!internalValue[0]) return;

    setInternalValue([internalValue[0], date]);
  };

  const handleChange = (newValue: Date | [Date, Date]): void => {
    if (naive) {
      if (Array.isArray(newValue)) {
        const [start, end] = newValue;
        return onChange([
          dayjs(start).startOf("day").add(dayjs(start).utcOffset(), "minute").toDate(),
          dayjs(end).startOf("day").add(dayjs(end).utcOffset(), "minute").toDate(),
        ]);
      }
      return onChange(dayjs(newValue).startOf("day").add(dayjs(newValue).utcOffset(), "minute").toDate());
    }
    if (type === DatePickerType.range) {
      return setStagedValue(newValue);
    }
    return onChange(newValue);
  };

  useEffect(() => {
    setStagedValue(undefined);
  }, [type, value]);

  // Either the minDate or the first selected date, whichever is larger
  const calculatedMinDate = new Date(
    Math.max(minDate?.getTime() ?? 0, internalValue ? internalValue[0]?.getTime() ?? 0 : 0),
  );

  if (view !== DateSelectorView.day && type === DatePickerType.range) {
    console.warn("Only the month view is supported for range selection. Any other view is undefined behaviour");
  }

  return (
    <div className="rounded-md bg-bg-base border border-border-base shadow-md w-full sm:w-auto sm:inline-block">
      {
        view === DateSelectorView.year && (
          <div className="m-2">
            <YearView
              // We want the "first date" to appear at the end
              startYear={(firstDate.getFullYear() - 11)}
              onNextSection={() => setFirstDate((firstDate) => new Date(firstDate.getFullYear() + 12, 0, 1))}
              onPreviousSection={() => setFirstDate((firstDate) => new Date(firstDate.getFullYear() - 12, 0, 1))}
              onSelectYear={(date) => {
                if (type === DatePickerType.single) {
                  handleChange(date);
                }
              }}
              minDate={minDate}
              maxDate={maxDate}
              selected={internalValue ?? value}
            />
          </div>
        )
      }
      {
        view === DateSelectorView.month && (
          <div className="p-2">
            <MonthView
              // We want the "first date" to appear at the end
              year={firstDate.getFullYear() ?? 0}
              onSelectMonth={(date) => {
                if (type === DatePickerType.single) {
                  handleChange(date);
                }
              }}
              minDate={minDate}
              maxDate={maxDate}
              selected={internalValue ?? value}
              onPreviousYear={() => setFirstDate((firstDate) => new Date(firstDate.getFullYear() - 1, 0, 1))}
              onNextYear={() => setFirstDate((firstDate) => new Date(firstDate.getFullYear() + 1, 0, 1))}
            />
          </div>
        )
      }
      {view === DateSelectorView.day && (
        <div className="flex flex-col">
          <div className={`p-2 ${type === DatePickerType.single ? "" : "sm:hidden"}`}>
            <DayView
              month={(firstDate.getMonth() + 1)}
              year={firstDate.getFullYear() ?? 0}
              minDate={minDate}
              maxDate={maxDate}
              onPreviousMonth={() => setFirstDate((firstDate) => new Date(firstDate.getFullYear(), firstDate.getMonth() - 1, 1))}
              onNextMonth={() => setFirstDate((firstDate) => new Date(firstDate.getFullYear(), firstDate.getMonth() + 1, 1))}
              selected={internalValue ?? stagedValue ?? value}
              showOutsideDays={type === DatePickerType.single}
              onHoverDate={handleHoverDate}
              onSelectDate={handleSelectDate}
            />
          </div>
          <div className={`hidden ${type === DatePickerType.range ? "sm:flex" : ""} flex-row flex-nowrap`}>
            <div className="p-2 border-r border-border-base">
              <DayView
                month={(firstDate.getMonth() + 1)}
                year={firstDate.getFullYear() ?? 0}
                minDate={calculatedMinDate}
                maxDate={maxDate}
                onPreviousMonth={() => setFirstDate((firstDate) => new Date(firstDate.getFullYear(), firstDate.getMonth() - 1, 1))}
                selected={internalValue ?? stagedValue ?? value}
                showOutsideDays={false}
                onHoverDate={handleHoverDate}
                onSelectDate={handleSelectDate}
              />
            </div>
            <div className="p-2">
              <DayView
                month={(firstDate.getMonth() + 2)}
                year={firstDate.getFullYear() ?? 0}
                minDate={calculatedMinDate}
                maxDate={maxDate}
                onNextMonth={() => setFirstDate((firstDate) => new Date(firstDate.getFullYear(), firstDate.getMonth() + 1, 1))}
                selected={internalValue ?? stagedValue ?? value}
                showOutsideDays={false}
                onHoverDate={handleHoverDate}
                onSelectDate={handleSelectDate}
              />
            </div>
          </div>
          {type === DatePickerType.range && (
            <>
              <Divider length="full" />
              <div className="w-full bg-bg-subtle flex justify-end gap-x-2 p-4">
                <Button
                  type="neutral"
                  size="small"
                  onClick={() => {
                    setStagedValue(undefined);
                    setInternalValue(undefined);
                    onCancel?.();
                  }}
                >
                  {cancelText || "Cancel"}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    onChange(stagedValue as [Date, Date]);
                  }}
                  disabled={!stagedValue}
                >
                  {okText || "Confirm"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DateSelector;
